import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { createLabOrder } from '../../api/encounters.js'
import { searchLabTests } from '../../api/catalogues.js'
import OrgSelect from '../../components/OrgSelect.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CatalogueAutocomplete from './CatalogueAutocomplete.jsx'
import { Card, CardHeader } from '../../components/Card.jsx'
import { statusColor, formatDateTime } from '../../utils/format.js'
import OrderResultsPanel from './OrderResultsPanel.jsx'
import { URGENCY } from '../../constants/index.js'
import usePersistedState from '../../hooks/usePersistedState.js'

export default function LabOrderForm({ encounter, locked }) {
  const qc = useQueryClient()
  // Persist transient draft to localStorage so half-built lab orders
  // survive HMR / tab close / re-mounts. Cleared on successful submit.
  const draftKey = `lab:enc${encounter.id}`
  const [labId, setLabId] = usePersistedState(`${draftKey}:lab`, null)
  const [clinicalNotes, setClinicalNotes] = usePersistedState(`${draftKey}:notes`, '')
  const [items, setItems] = usePersistedState(`${draftKey}:items`, [])
  const [formError, setFormError] = useState(null)

  const mut = useMutation({
    mutationFn: (body) => createLabOrder(encounter.id, body),
    onSuccess: (newOrder) => {
      // Splice the new order into the encounter cache directly from the
      // POST response — saves a second round-trip that invalidate +
      // refetch would have triggered. Falls back to invalidate if the
      // cache shape isn't quite right.
      qc.setQueryData(['encounter', String(encounter.id)], (prev) => {
        if (!prev || !newOrder) return prev
        return { ...prev, lab_orders: [...(prev.lab_orders || []), newOrder] }
      })
      qc.invalidateQueries({ queryKey: ['encounter', String(encounter.id)] })
      setItems([]); setClinicalNotes('')
    },
  })

  function addTest(t) {
    setItems((curr) => {
      if (curr.some((x) => x.test_code === t.code)) return curr
      return [...curr, { test_code: t.code, test_name: t.name, loinc_code: t.loinc_code || null, urgency: 'routine' }]
    })
  }
  function updateItem(i, patch) {
    setItems((curr) => curr.map((x, idx) => idx === i ? { ...x, ...patch } : x))
  }
  function removeItem(i) {
    setItems((curr) => curr.filter((_, idx) => idx !== i))
  }

  function onSubmit(e) {
    e.preventDefault()
    setFormError(null)
    if (!labId) return setFormError('Select a target laboratory.')
    if (items.length === 0) return setFormError('Add at least one test.')
    mut.mutate({
      target_lab_id: labId,
      clinical_notes: clinicalNotes,
      items,
    })
  }

  const existing = encounter.lab_orders || []

  return (
    <Card>
      <CardHeader title="Lab orders" subtitle="Route to a laboratory on submit" />

      {existing.length > 0 && (
        <div className="space-y-3 mb-4">
          {existing.map((lo) => (
            <div key={lo.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase ${statusColor(lo.status)}`}>{lo.status}</span>
                <span className="font-mono text-slate-500">#{lo.id}</span>
                {lo.laboratory_name && (
                  <span className="text-slate-700"><span className="text-slate-400">→</span> {lo.laboratory_name}</span>
                )}
                {lo.created_at && <span className="text-slate-400">· Ordered {formatDateTime(lo.created_at)}</span>}
              </div>

              {/*
                Compact test list: one labelled line that flows
                naturally with the order card. We hide LOINC/codes and
                per-test urgency — those are useful at order time but
                visual noise once the order is placed. The full detail
                is preserved on the lab side and on the generated PDF.
              */}
              {(lo.items || []).length > 0 && (
                <div className="mt-2 text-sm text-slate-700">
                  <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Tests</span>
                  {lo.items.map((it, i) => (
                    <span key={it.id || it.test_code || i}>
                      {i > 0 && <span className="text-slate-300 mx-1">·</span>}
                      <span className="font-medium">{it.test_name}</span>
                    </span>
                  ))}
                </div>
              )}

              {lo.clinical_notes && (
                <div className="mt-2 text-[11px] text-slate-500">
                  <span className="uppercase tracking-wide text-slate-400">Clinical notes </span>
                  {lo.clinical_notes}
                </div>
              )}

              {/*
                Results & attachments — fetched on demand from the
                ClinicalDocument API, scoped to this lab order. Shows
                the laboratory's typed result_summary alongside any
                uploaded PDFs / images with Open + Download buttons,
                so the doctor can review and act on results without
                leaving the editor. Hidden entirely when the order is
                still pending and nothing has been uploaded.
              */}
              <OrderResultsPanel
                kind="lab"
                encounter={encounter}
                orderId={lo.id}
                status={lo.status}
                resultStatus={lo.result_status}
                summary={lo.result_summary}
                prelim={lo.preliminary_result_summary}
                comment={lo.result_comment}
                ordered={(lo.items || []).map((it) => ({ label: it.test_name }))}
                items={lo.items || []}
                subtitle={lo.laboratory_name ? `Issued by ${lo.laboratory_name}` : 'Issued by the laboratory'}
              />
            </div>
          ))}
        </div>
      )}

      {locked ? (
        <div className="text-xs text-slate-400">
          {existing.length === 0
            ? 'No lab orders were recorded on this encounter.'
            : 'Encounter is locked. Add new orders via an amended encounter.'}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Target laboratory</span>
            <div className="mt-1">
              <OrgSelect orgType="laboratory" value={labId} onChange={setLabId} placeholder="Select lab…" />
            </div>
          </label>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Add test</span>
            <div className="mt-1">
              <CatalogueAutocomplete
                queryKey="lab-tests"
                searchFn={searchLabTests}
                placeholder="Search lab tests by name, short code (CBC, MAL-RDT…), category, or LOINC…"
                rowKey={(t) => t.code}
                renderRow={(t) => (
                  <>
                    <span className="font-mono text-xs text-wantok-700">{t.short_code || t.code}</span>
                    <span className="ml-2 font-medium">{t.name}</span>
                    {t.sample_type && <span className="ml-2 text-xs text-slate-500 italic">{t.sample_type}</span>}
                    {t.category && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-slate-50 text-slate-600 border-slate-200">{t.category}</span>}
                    {t.typical_turnaround && <span className="ml-2 text-[10px] text-slate-500">TAT {t.typical_turnaround}</span>}
                  </>
                )}
                onPick={addTest}
              />
            </div>
          </label>

          {items.length > 0 && (
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it.test_code} className="border border-slate-200 rounded p-2 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-mono text-xs text-wantok-700">{it.test_code}</span>
                    <span className="flex-1 truncate font-medium">{it.test_name}</span>
                    <select value={it.urgency} onChange={(e) => updateItem(i, { urgency: e.target.value })} className="border rounded px-1 py-1 text-xs">
                      {URGENCY.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <button type="button" onClick={() => removeItem(i)} className="text-xs text-rose-600">×</button>
                  </div>
                  {/* Inline availability + price + TAT chip at the chosen lab.
                      Only renders when the doctor picked from the catalogue
                      (test_code is the LabTest.id). */}
                  {it.test_code && (
                    <LabAvailabilityHint testId={it.test_code} laboratoryId={labId} />
                  )}
                </div>
              ))}
            </div>
          )}

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">Clinical notes for lab</span>
            <textarea rows={2} value={clinicalNotes} onChange={(e) => setClinicalNotes(e.target.value)} className="mt-1 w-full border rounded px-2 py-1.5 text-sm" />
          </label>

          {(formError || mut.error) && <ErrorBanner error={formError || mut.error} />}

          <div className="flex justify-end">
            <button disabled={mut.isPending} className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60">
              {mut.isPending ? 'Saving…' : 'Add lab order'}
            </button>
          </div>
        </form>
      )}
    </Card>
  )
}

/**
 * Inline availability + price + TAT hint for a picked lab test at the
 * chosen target laboratory. Tells the doctor whether the lab actually
 * runs the test, and at what price — so they don't send a sample to a
 * lab that can't process it.
 */
function LabAvailabilityHint({ testId, laboratoryId }) {
  const stockQ = useQuery({
    queryKey: ['lab-test-stock', testId],
    queryFn: async () => {
      const { data } = await api.get(`${endpoints.labTestSearch}${testId}/stock/`)
      return data
    },
    enabled: Boolean(testId),
    staleTime: 30_000,
  })
  if (stockQ.isLoading) {
    return <div className="text-[11px] text-slate-400 italic">Checking lab availability…</div>
  }
  if (stockQ.error || !Array.isArray(stockQ.data)) return null
  const rows = stockQ.data
  if (rows.length === 0) {
    return (
      <div className="text-[11px] bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1">
        No laboratory on the platform currently offers this test. The order
        will still send; the receiving lab can run it as a send-out or refuse.
      </div>
    )
  }
  const target = laboratoryId != null ? Number(laboratoryId) : null
  const here = target != null ? rows.find((r) => Number(r.laboratory) === target) : null
  const others = rows.filter((r) => Number(r.laboratory) !== target)

  if (here) {
    return (
      <div className="text-[11px] bg-emerald-50 border border-emerald-200 text-emerald-900 rounded px-2 py-1 flex flex-wrap items-center gap-2">
        <span className="font-medium">✓ Offered at this laboratory</span>
        {here.price != null && <span>· PGK <b>{here.price}</b></span>}
        {here.turnaround_time && <span>· TAT <b>{here.turnaround_time}</b></span>}
      </div>
    )
  }
  return (
    <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1">
      <div className="font-medium">
        ⚠ {laboratoryId ? 'Not offered at the laboratory you picked.' : 'Pick a laboratory to see availability.'}
        {others.length > 0 && ` Offered elsewhere by ${others.length} lab${others.length === 1 ? '' : 's'}:`}
      </div>
      {others.length > 0 && (
        <ul className="mt-1 ml-2">
          {others.slice(0, 5).map((r) => (
            <li key={r.id}>
              · {r.laboratory_name}
              {r.price != null && <> — PGK {r.price}</>}
              {r.turnaround_time && <> · TAT {r.turnaround_time}</>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
